import {
  Star,
  MapPin,
  Clock,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  Send,
  Users,
  Award,
  FileText,
  Phone,
  Calendar
} from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import type { ProviderProfile, ProviderMedia } from '../types/provider-profile.types';
import { hasValue, hasArrayData } from '../utils/fieldValidation';
import { getProviderDisplayName, getProviderDisplayInitial } from '../utils/formatters';

type ProviderDetailsHeroProps = {
  provider: ProviderProfile;
  providerMedia?: ProviderMedia[];
  displayRating: number | null;
  displayReviewCount: number;
  reviewsLoading: boolean;
  serviceRangesText?: string;
  canFavorite: boolean;
  canAddToNetwork: boolean;
  canReferProvider: boolean;
  currentFavoriteState: boolean;
  currentNetworkState: boolean;
  onFavoriteToggle: () => void;
  onNetworkToggle: () => void;
  onReferProvider?: () => void;
  /**
   * Feature 071 — share this provider's card with someone who is not on bQuest.
   * Rendered only when supplied; the parent decides who is eligible.
   */
  onShareContactCard?: () => void;
  onMessageProvider: () => void;
  onShareProvider?: () => void;
  onRequestConsultation?: () => void;
  /**
   * Feature 102 — secondary "Schedule Consultation" (Calendly) action,
   * rendered alongside "Request a call" when the provider has a calendlyUrl.
   */
  onScheduleConsultation?: () => void;
  isFavoriteLoading: boolean;
  isNetworkLoading: boolean;
};

export const ProviderDetailsHero = ({
  provider,
  providerMedia,
  displayRating,
  displayReviewCount,
  reviewsLoading,
  serviceRangesText,
  canFavorite,
  canAddToNetwork,
  canReferProvider,
  currentFavoriteState,
  currentNetworkState,
  onFavoriteToggle,
  onNetworkToggle,
  onReferProvider,
  onShareContactCard,
  onMessageProvider,
  onShareProvider,
  onRequestConsultation,
  onScheduleConsultation,
  isFavoriteLoading,
  isNetworkLoading,
}: ProviderDetailsHeroProps) => {
  const specialtiesText = provider.specialties?.slice(0, 3)?.map(s => s.name)?.join(', ') || '';
  const deliveryTypes = provider.serviceDeliveryTypes || serviceRangesText || '';
  const ratingValue = displayRating ?? 0;

  // FR-007e: Display name fallback chain - partnerCompany.name → companyName → (firstName + lastName)
  const displayName = getProviderDisplayName(provider.partnerCompany, provider.companyName, provider.user);
  const displayInitial = getProviderDisplayInitial(provider.partnerCompany, provider.companyName, provider.user);

  // Feature 041: Find primary media from gallery
  // Priority: isPrimary === true → first item by displayOrder (fallback)
  const primaryMedia = (() => {
    if (!providerMedia?.length) return null;
    const primary = providerMedia.find(m => m.isPrimary);
    if (primary) return primary;
    // Fallback: use first item sorted by displayOrder
    return [...providerMedia].sort((a, b) => a.displayOrder - b.displayOrder)[0];
  })();

  // Handle share action
  const handleShare = () => {
    if (onShareProvider) {
      onShareProvider();
    } else {
      // Default share behavior - copy to clipboard
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        // Could show a toast notification here
        console.log('URL copied to clipboard');
      });
    }
  };

  return (
    <Card className="overflow-hidden mb-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Hero Media Section */}
        <div className="relative h-96">
          {/* Feature 041: Display primary media (isPrimary) from gallery, fallback to profile photo, then hero media */}
          {primaryMedia ? (
            primaryMedia.mediaType === 'video' ? (
              <video
                data-testid="primary-media-video"
                src={primaryMedia.mediaUrl}
                className="w-full h-full object-contain bg-gray-50"
                controls
                autoPlay
                muted
                loop
              />
            ) : (
              <img
                src={primaryMedia.mediaUrl}
                alt={primaryMedia.caption || `${displayName} profile`}
                loading="lazy"
                className="w-full h-full object-contain bg-gray-50"
              />
            )
          ) : provider.user?.profilePhoto ? (
            <img
              src={provider.user.profilePhoto}
              alt={`${displayName} profile`}
              loading="lazy"
              className="w-full h-full object-contain bg-gray-50"
            />
          ) : provider.heroMediaUrl && provider.mediaType === 'video' ? (
            <video
              data-testid="hero-video"
              src={provider.heroMediaUrl}
              className="w-full h-full object-cover"
              controls
              autoPlay
              muted
              loop
            />
          ) : provider.heroMediaUrl && provider.mediaType === 'image' ? (
            <img
              src={provider.heroMediaUrl}
              alt={displayName}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full bg-gradient-to-br from-[#026857] to-[#378F94] flex items-center justify-center"
            >
              <div className="text-white text-6xl font-bold">
                {displayInitial}
              </div>
            </div>
          )}
          
          {/* Action Buttons Overlay */}
          <div className="absolute top-4 right-4 flex gap-2">
            {canFavorite && (
              <Button 
                size="sm" 
                variant="secondary" 
                className="bg-white/90 hover:bg-white"
                onClick={onFavoriteToggle}
                disabled={isFavoriteLoading}
                aria-label={currentFavoriteState ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart 
                  className={`w-4 h-4 ${currentFavoriteState ? 'fill-red-500 text-red-500' : ''}`} 
                />
              </Button>
            )}
            <Button 
              size="sm" 
              variant="secondary" 
              className="bg-white/90 hover:bg-white"
              onClick={handleShare}
              aria-label="Share provider"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Info Section */}
        <div className="p-8">
          <div className="mb-4">
            {/* Specialties and Service Delivery Types - Graceful degradation */}
            {(hasValue(specialtiesText) || hasValue(deliveryTypes)) && (
              <div className="mb-4">
                {hasValue(specialtiesText) && (
                  <p className="text-sm text-gray-600 mb-2">{specialtiesText}</p>
                )}
                {hasValue(deliveryTypes) && (
                  <p className="text-sm text-gray-600 mb-2">{deliveryTypes}</p>
                )}
              </div>
            )}
            
            {/* Company Name (FR-007e: uses fallback chain) */}
            <h1 className="text-3xl text-gray-900 mb-2">{displayName}</h1>
            
            {/* T027: Display tagline - Graceful degradation */}
            {hasValue(provider.tagline) && (
              <p className="text-lg text-gray-600 mb-4">{provider.tagline}</p>
            )}
            
            {/* T033: Rating stars with fill logic - shows Google Places rating when available */}
            {/* Graceful degradation: only show if provider has placeId (Google integration) */}
            {hasValue(provider.placeId) && (
              <div className="flex items-center gap-2 mb-4">
                {reviewsLoading ? (
                  <div className="flex items-center gap-2 text-gray-400">
                    <span className="text-sm">Loading reviews...</span>
                  </div>
                ) : ratingValue > 0 ? (
                  <div className="flex items-center gap-1">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          data-testid={i < Math.floor(ratingValue) ? 'star-filled' : 'star-empty'}
                          className={`w-5 h-5 ${
                            i < Math.floor(ratingValue)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-gray-900 ml-1">{ratingValue.toFixed(1)}</span>
                    <span className="text-gray-500">({displayReviewCount} reviews)</span>
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">No reviews available</span>
                )}
              </div>
            )}

            {/* Key Information - Graceful degradation: hide section if no data */}
            {(hasArrayData(provider.servedCounties) ||
              hasValue(provider.hoursDescription) ||
              hasValue(provider.priceRangeIndicator) ||
              hasValue(provider.yearsInBusiness) ||
              hasValue(provider.establishedYear)) && (
              <div className="space-y-2 mb-6">
                {/* T029: Served counties display */}
                {hasArrayData(provider.servedCounties) && (
                  <div className="flex items-start gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1 text-sm">
                      {provider.servedCounties.map((county, idx) => (
                        <span key={idx}>
                          {county}
                          {idx < provider.servedCounties.length - 1 && <span className="mr-1">,</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* T031: Hours description display */}
                {hasValue(provider.hoursDescription) && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="w-4 h-4 text-gray-400" data-testid="clock-icon" />
                    <span className="text-sm">{provider.hoursDescription}</span>
                  </div>
                )}
                
                {/* T030: Pricing information display - Feature 038: Show only price range indicator */}
                {hasValue(provider.priceRangeIndicator) && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <div className="text-sm">
                      <span className="font-semibold">{provider.priceRangeIndicator}</span>
                    </div>
                  </div>
                )}

                {/* T032: Years in business and established year */}
                {(hasValue(provider.yearsInBusiness) || hasValue(provider.establishedYear)) && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Award className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">
                      {hasValue(provider.yearsInBusiness) && `${provider.yearsInBusiness} years in business`}
                      {hasValue(provider.yearsInBusiness) && hasValue(provider.establishedYear) && ' • '}
                      {hasValue(provider.establishedYear) && `Established ${provider.establishedYear}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mb-3">
              <Button
                className="flex-1 bg-[#026857] hover:bg-[#025247]"
                onClick={onMessageProvider}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Message Provider
              </Button>
            </div>

            {/* Feature 102: Request a call (primary care-seeker action).
                The Key intake-form providers keep their original button
                unchanged (Feature 103 bypass). */}
            {canFavorite && onRequestConsultation && (
              <div className="mb-3">
                <Button
                  className="w-full bg-[#378F94] hover:bg-[#2e7a7f]"
                  onClick={onRequestConsultation}
                >
                  {provider.intakeFormUrl ? (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      {provider.calendlyUrl ? 'Schedule Consultation' : 'Request Consultation'}
                    </>
                  ) : (
                    <>
                      <Phone className="w-4 h-4 mr-2" />
                      Request a call
                    </>
                  )}
                </Button>
                {/* Feature 102: secondary Calendly scheduling when available */}
                {!provider.intakeFormUrl && provider.calendlyUrl && onScheduleConsultation && (
                  <Button
                    variant="outline"
                    className="w-full mt-3"
                    onClick={onScheduleConsultation}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Consultation
                  </Button>
                )}
              </div>
            )}
            
            {/* Role-based buttons */}
            <div className="flex gap-3">
              {canFavorite && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onFavoriteToggle}
                  disabled={isFavoriteLoading}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Save this Provider
                </Button>
              )}
              {canAddToNetwork && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onNetworkToggle}
                  disabled={isNetworkLoading}
                >
                  <Users className="w-4 h-4 mr-2" />
                  {currentNetworkState ? 'Remove from Network' : 'Add to Network'}
                </Button>
              )}
            </div>

            {/* Feature 001: Refer Provider button for Financial Advisors */}
            {canReferProvider && onReferProvider && (
              <div className="mt-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={onReferProvider}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Refer Provider to Clients
                </Button>
              </div>
            )}

            {/* Feature 071: send this provider's card to someone off-platform */}
            {onShareContactCard && (
              <div className="mt-3">
                <Button variant="outline" className="w-full" onClick={onShareContactCard}>
                  <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                  Send Contact Card
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

